// ML Predictions Display Component
// Displays ML-powered fill probabilities and regime detection

import React from 'react';
import { Brain, Clock, TrendingUp, TrendingDown, Activity, AlertCircle, CheckCircle, Info } from 'lucide-react';
import type { MLData } from '../../types';

interface MLPredictionsDisplayProps {
  mlData: MLData | null | undefined;
}

export function MLPredictionsDisplay({ mlData }: MLPredictionsDisplayProps) {
  if (!mlData || !mlData.enabled) {
    return (
      <div style={{
        backgroundColor: '#10151b',
        padding: 16,
        borderRadius: 8,
        border: '1px solid #2a3540',
        textAlign: 'center',
        color: '#7e8a96'
      }}>
        <Brain size={24} style={{ marginBottom: 8, opacity: 0.5 }} />
        <div style={{ fontSize: 12 }}>
          {mlData?.error ? `ML Error: ${mlData.error}` :
           mlData?.reason ? `ML Disabled: ${mlData.reason}` :
           'ML predictions not available'}
        </div>
      </div>
    );
  }

  const getRegimeColor = (regime: string) => {
    switch (regime) {
      case 'trending': return '#36c98e'; // green
      case 'neutral': return '#ff8c3a'; // yellow/amber
      case 'volatile': return '#ff5c5c'; // red
      case 'sideways': return '#c77dff'; // purple
      default: return '#7e8a96'; // gray
    }
  };

  const getRegimeIcon = (regime: string) => {
    switch (regime) {
      case 'trending': return <TrendingUp size={14} />;
      case 'volatile': return <Activity size={14} />;
      case 'sideways': return <TrendingDown size={14} />;
      default: return <Info size={14} />;
    }
  };

  const getConfidenceColor = (confidence: 'low' | 'medium' | 'high') => {
    switch (confidence) {
      case 'high': return '#36c98e';
      case 'medium': return '#ff8c3a';
      default: return '#ff5c5c';
    }
  };

  const getConfidenceIntensity = (confidence: 'low' | 'medium' | 'high') => {
    switch (confidence) {
      case 'high': return 1.0;
      case 'medium': return 0.6;
      default: return 0.3;
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 16
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8
      }}>
        <Brain size={16} color="#c77dff" />
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#d7dde4' }}>
          ML Predictions
        </h4>
      </div>

      {/* Regime Detection */}
      {mlData.regime && (
        <div style={{
          backgroundColor: '#10151b',
          padding: 16,
          borderRadius: 8,
          border: '1px solid #2a3540'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12
          }}>
            <h5 style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#7e8a96' }}>
              Market Regime
            </h5>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              backgroundColor: `${getRegimeColor(mlData.regime.regime)}15`,
              border: `1px solid ${getRegimeColor(mlData.regime.regime)}30`,
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: 11,
              fontWeight: 600,
              color: getRegimeColor(mlData.regime.regime)
            }}>
              {getRegimeIcon(mlData.regime.regime)}
              {mlData.regime.regime.toUpperCase()}
            </div>
          </div>

          {/* Regime Confidence Bar */}
          <div style={{ marginBottom: 12 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 11,
              color: '#7e8a96',
              marginBottom: 4
            }}>
              <span>Confidence</span>
              <span style={{ fontWeight: 600, color: '#d7dde4' }}>
                {(mlData.regime.confidence * 100).toFixed(0)}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: 8,
              backgroundColor: '#0a0d10',
              borderRadius: 4,
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${mlData.regime.confidence * 100}%`,
                height: '100%',
                backgroundColor: getRegimeColor(mlData.regime.regime),
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>

          {/* Regime Features */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 8,
            fontSize: 11
          }}>
            <div>
              <div style={{ color: '#7a8693', marginBottom: 2 }}>Spread Asymmetry</div>
              <div style={{ color: '#d7dde4', fontWeight: 600 }}>
                {mlData.regime.regime_features.spread_asymmetry.toFixed(3)}
              </div>
            </div>
            <div>
              <div style={{ color: '#7a8693', marginBottom: 2 }}>Aggression Factor</div>
              <div style={{ color: '#d7dde4', fontWeight: 600 }}>
                {mlData.regime.regime_features.aggression_factor.toFixed(2)}x
              </div>
            </div>
            <div>
              <div style={{ color: '#7a8693', marginBottom: 2 }}>Optimal Spread</div>
              <div style={{ color: '#d7dde4', fontWeight: 600 }}>
                {mlData.regime.regime_features.optimal_spread_multiplier.toFixed(2)}x
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fill Probabilities */}
      {mlData.fill_probability && (
        <div style={{
          backgroundColor: '#10151b',
          padding: 16,
          borderRadius: 8,
          border: '1px solid #2a3540'
        }}>
          <h5 style={{ margin: '0 0 16px 0', fontSize: 12, fontWeight: 600, color: '#7e8a96' }}>
            Fill Probabilities
          </h5>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Buy Probabilities */}
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 12,
                fontSize: 11,
                fontWeight: 600,
                color: '#36c98e'
              }}>
                <TrendingUp size={12} />
                BUY ORDERS
              </div>

              {/* Buy at Best Ask */}
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#7a8693',
                  marginBottom: 4
                }}>
                  <span>At Best Ask</span>
                  <span style={{ fontWeight: 600, color: '#d7dde4' }}>
                    {(mlData.fill_probability.buy.at_best_ask * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: 6,
                  backgroundColor: '#0a0d10',
                  borderRadius: 3,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${mlData.fill_probability.buy.at_best_ask * 100}%`,
                    height: '100%',
                    backgroundColor: '#36c98e',
                    opacity: getConfidenceIntensity(mlData.fill_probability.details.buy_at_ask.confidence),
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                {mlData.fill_probability.details.buy_at_ask && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 4,
                    fontSize: 9,
                    color: '#7a8693'
                  }}>
                    <Clock size={10} />
                    <span>{mlData.fill_probability.details.buy_at_ask.estimated_wait_time.toFixed(0)}s</span>
                    <div style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: getConfidenceColor(mlData.fill_probability.details.buy_at_ask.confidence)
                    }} />
                    <span>{mlData.fill_probability.details.buy_at_ask.confidence}</span>
                  </div>
                )}
              </div>

              {/* Buy at Mid */}
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#7a8693',
                  marginBottom: 4
                }}>
                  <span>At Mid</span>
                  <span style={{ fontWeight: 600, color: '#d7dde4' }}>
                    {(mlData.fill_probability.buy.at_mid * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: 6,
                  backgroundColor: '#0a0d10',
                  borderRadius: 3,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${mlData.fill_probability.buy.at_mid * 100}%`,
                    height: '100%',
                    backgroundColor: '#36c98e',
                    opacity: 0.6,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {/* Buy at Best Bid */}
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#7a8693',
                  marginBottom: 4
                }}>
                  <span>At Best Bid</span>
                  <span style={{ fontWeight: 600, color: '#d7dde4' }}>
                    {(mlData.fill_probability.buy.at_best_bid * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: 6,
                  backgroundColor: '#0a0d10',
                  borderRadius: 3,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${mlData.fill_probability.buy.at_best_bid * 100}%`,
                    height: '100%',
                    backgroundColor: '#36c98e',
                    opacity: 0.4,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            </div>

            {/* Sell Probabilities */}
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 12,
                fontSize: 11,
                fontWeight: 600,
                color: '#ff5c5c'
              }}>
                <TrendingDown size={12} />
                SELL ORDERS
              </div>

              {/* Sell at Best Bid */}
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#7a8693',
                  marginBottom: 4
                }}>
                  <span>At Best Bid</span>
                  <span style={{ fontWeight: 600, color: '#d7dde4' }}>
                    {(mlData.fill_probability.sell.at_best_bid * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: 6,
                  backgroundColor: '#0a0d10',
                  borderRadius: 3,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${mlData.fill_probability.sell.at_best_bid * 100}%`,
                    height: '100%',
                    backgroundColor: '#ff5c5c',
                    opacity: getConfidenceIntensity(mlData.fill_probability.details.sell_at_bid.confidence),
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                {mlData.fill_probability.details.sell_at_bid && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 4,
                    fontSize: 9,
                    color: '#7a8693'
                  }}>
                    <Clock size={10} />
                    <span>{mlData.fill_probability.details.sell_at_bid.estimated_wait_time.toFixed(0)}s</span>
                    <div style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: getConfidenceColor(mlData.fill_probability.details.sell_at_bid.confidence)
                    }} />
                    <span>{mlData.fill_probability.details.sell_at_bid.confidence}</span>
                  </div>
                )}
              </div>

              {/* Sell at Mid */}
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#7a8693',
                  marginBottom: 4
                }}>
                  <span>At Mid</span>
                  <span style={{ fontWeight: 600, color: '#d7dde4' }}>
                    {(mlData.fill_probability.sell.at_mid * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: 6,
                  backgroundColor: '#0a0d10',
                  borderRadius: 3,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${mlData.fill_probability.sell.at_mid * 100}%`,
                    height: '100%',
                    backgroundColor: '#ff5c5c',
                    opacity: 0.6,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {/* Sell at Best Ask */}
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#7a8693',
                  marginBottom: 4
                }}>
                  <span>At Best Ask</span>
                  <span style={{ fontWeight: 600, color: '#d7dde4' }}>
                    {(mlData.fill_probability.sell.at_best_ask * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: 6,
                  backgroundColor: '#0a0d10',
                  borderRadius: 3,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${mlData.fill_probability.sell.at_best_ask * 100}%`,
                    height: '100%',
                    backgroundColor: '#ff5c5c',
                    opacity: 0.4,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          {mlData.fill_probability.details && (
            <div style={{
              marginTop: 16,
              paddingTop: 12,
              borderTop: '1px solid #2a3540',
              fontSize: 10,
              color: '#7a8693'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {mlData.fill_probability.details.buy_at_ask && (
                  <div>
                    <div style={{ marginBottom: 4 }}>Buy Queue Position</div>
                    <div style={{ color: '#d7dde4', fontWeight: 600 }}>
                      #{mlData.fill_probability.details.buy_at_ask.queue_position.toFixed(2)}
                    </div>
                  </div>
                )}
                {mlData.fill_probability.details.sell_at_bid && (
                  <div>
                    <div style={{ marginBottom: 4 }}>Sell Queue Position</div>
                    <div style={{ color: '#d7dde4', fontWeight: 600 }}>
                      #{mlData.fill_probability.details.sell_at_bid.queue_position.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



